import "@/App.css";
// 29/05 — Import en tete pour garantir l'installation des intercepteurs axios
// (refresh JWT auto sur 401) AVANT le 1er polling TaskContext / AuthContext.
import "./lib/api";
import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, RH_ROLES } from "./contexts/AuthContext";
import { TaskProvider } from "./contexts/TaskContext";
import { PrefsProvider } from "./contexts/PrefsContext";
// 27/10/2026 (suivi RH, lot 5) — réglage RH commun : listes de choix et couleurs du thème (variables --rh-*).
import { ReglagesRhProvider } from "./contexts/ReglagesRhContext";
import { Toaster } from "./components/ui/sonner";
import DialogueCadenasVerrouille from "./components/cadenas/DialogueCadenasVerrouille";

// 11/10/2026 — la page cachée /1806 (« Gouvernance Cercle »), son raccourci
// Cmd/Ctrl + Maj + G et ses mails sont SUPPRIMÉS (décision de Théo : « la page
// cachée /1806 et le mail tu supprimes tout » ; docs/SUPPRESSION_1806_11_10.md).
// Depuis le 22/10/2026, Pilotage et Déposer des pièces s'ouvrent au RÔLE fondateur, sans code à
// 6 chiffres ; seule la page Contrôle IA garde le code (gc/DeverrouillageFondateur.js).

// Code splitting : chaque page est chargee en chunk separe
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const InboxPage = React.lazy(() => import("./pages/InboxPage"));
const SuiviDirectionPage = React.lazy(() => import("./pages/SuiviDirectionPage"));  // 04/10/2026 — suivi des tâches de la direction
const IAHubPage = React.lazy(() => import("./pages/IAHubPage"));
const DashboardLayout = React.lazy(() => import("./pages/DashboardLayout"));
const DashboardHome = React.lazy(() => import("./pages/DashboardHome"));
// 30/04 — Refonte unifiée Théo : 1 page CRM avec tabs (pattern Rentabilité)
const CRMUnifiedPage = React.lazy(() => import("./pages/CRMUnifiedPage"));
// v3/F1 (11/10/2026) — page Commercial v3 sur /crm ; l'ancienne page CRM reste à /crm/ancien pendant un mois.
const CommercialPage = React.lazy(() => import("./pages/CommercialPage"));
// Phase 4 Agent 4-1 — page dediee aux campagnes email (legacy, conservée pour rollback)
const HRPage = React.lazy(() => import("./pages/HRPage"));
const EmployeesPage = React.lazy(() => import("./pages/EmployeesPage"));
// Chantier hub — fiche collaborateur agrégée (consomme /api/employees/:id/full)
const FicheCollaborateur = React.lazy(() => import("./pages/FicheCollaborateur"));
const ComplianceDashboard = React.lazy(() => import("./pages/ComplianceDashboard"));
const DocumentsPage = React.lazy(() => import("./pages/DocumentsPage"));
const CommunicationPage = React.lazy(() => import("./pages/CommunicationPage"));
const DPAEPage = React.lazy(() => import("./pages/DPAEPage"));
const Clients = React.lazy(() => import("./pages/Clients"));
const ClientForm = React.lazy(() => import("./pages/ClientForm"));
// 07/09 — la FICHE client (maquette validée) : tout au même endroit, en LECTURE.
// Le formulaire garde son adresse à lui (/clients/:id/edit), comme devis/factures.
const FicheClient = React.lazy(() => import("./pages/FicheClient"));
const Sites = React.lazy(() => import("./pages/Sites"));
const SiteForm = React.lazy(() => import("./pages/SiteForm"));
// 01/09 — volet des fiches créées incomplètes par un import (sites/agents/clients).
const RentabilitePage = React.lazy(() => import("./pages/RentabilitePage"));
const SuiviHeuresPage = React.lazy(() => import("./pages/SuiviHeuresPage"));
const PaiePage = React.lazy(() => import("./pages/PaiePage.jsx"));  // Sprint paie 17/07 — coffre fiches de paie (fichier livré par la session 3, résolu à la fusion)
const SocialMediaPage = React.lazy(() => import("./pages/SocialMediaPage"));
const SignaturePage = React.lazy(() => import("./pages/SignaturePage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const RgpdAdminPage = React.lazy(() => import("./pages/RgpdAdminPage"));
const SettingsAdminPage = React.lazy(() => import("./pages/SettingsAdminPage"));
// RBAC-2 — gestion des comptes & rôles (fondateur uniquement)
const UsersAdminPage = React.lazy(() => import("./pages/UsersAdminPage"));
// Refonte 23/06 — module Administration consolidé (conteneur à sous-navigation)
const AdministrationPage = React.lazy(() => import("./pages/AdministrationPage"));
const AdminHealthPage = React.lazy(() => import("./pages/AdminHealthPage"));
const AdminArchitecturePage = React.lazy(() => import("./pages/AdminArchitecturePage"));
const ArbitrageHubPage = React.lazy(() => import("./pages/ArbitrageHubPage"));  // chantier C — arbitrage conflits/doublons
const AgentsDashboard = React.lazy(() => import("./pages/admin/AgentsDashboard"));  // MTJ Brain v1.0 ROCKET A
const AgentGovernancePage = React.lazy(() => import("./pages/admin/AgentGovernancePage"));  // Page de Contrôle des IA (fondateur L1)
const AlertesPoste = React.lazy(() => import("./pages/AlertesPoste"));
// L6.C — AgentPage, AgentExpertisePage, MultiAgentPage supprimés (redirects vers /ia)
// FeedbackSessionPage conservé car réutilisé par IAHub/IAValidationTab
const Planning = React.lazy(() => import("./pages/Planning"));
const TerrainPage = React.lazy(() => import("./pages/TerrainPage"));  // PHASE 4 — réception activité terrain
const TerrainAgentPage = React.lazy(() => import("./pages/TerrainAgentPage"));  // Retours 13/07 — activité terrain d'UN agent (page dédiée, hors fiche RH)
const PtiLeveeDoutePage = React.lazy(() => import("./pages/PtiLeveeDoutePage"));  // 14/10/2026 — page du lien de levée de doute (connexion exigée, résultat obligatoire pour clore)
const SitesTableauRemuneration = React.lazy(() => import("./pages/SitesTableauRemuneration"));  // 20/10/2026 — une ligne par site : rémunération et tarif, pré-remplis (GO Théo)
// Retours 13/07 — page Main courante UNIFIÉE (absorbe l'onglet MC de Terrain et
// la page Comptes-rendus, dont elle réutilise les fonctions génér/aperçu/envoi).
const MainCourantePage = React.lazy(() => import("./pages/MainCourantePage"));
const EcartsPointagePage = React.lazy(() => import("./pages/EcartsPointagePage"));  // 17/08 — arbitrage pointé ↔ planifié
const SondagesDispoPage = React.lazy(() => import("./pages/SondagesDispoPage"));  // SPRINT 2 (retour 14/07) — Sondages + Disponibilités UNIFIÉS (1 page, 2 onglets)
const Devis = React.lazy(() => import("./pages/Devis"));
const DevisForm = React.lazy(() => import("./pages/DevisForm"));
const Factures = React.lazy(() => import("./pages/Factures"));
const FacturesSousTraitants = React.lazy(() => import("./pages/FacturesSousTraitants"));
const DeposerPieces = React.lazy(() => import("./pages/DeposerPieces"));  // 06/10/2026 — lot C : porte de dépôt unique (fondateur seul, contrôlé par le serveur)
const PilotagePage = React.lazy(() => import("./pages/PilotagePage.jsx"));  // 13/10/2026 — Pilotage (maquette v8 validée par Théo), fondateur seul, contrôlé par le serveur
const FactureForm = React.lazy(() => import("./pages/FactureForm"));
const AgentNetworkPage = React.lazy(() => import("./pages/AgentNetworkPage"));
const AOReponsePage = React.lazy(() => import("./pages/AOReponsePage"));
// 30/04 — Refonte unifiée Théo : 1 page AO avec tabs (Liste / Réponse / Base de Réponses / Importer)
const AppelsOffreUnifiedPage = React.lazy(() => import("./pages/AppelsOffreUnifiedPage"));
// FeedbackSessionPage n'est plus lazy-routé (IAHub l'importe en sync via IAValidationTab)
// MultiAgentPage supprimé (dead code)
// import DuplicatesPage from "./pages/DuplicatesPage"; // Route supprimée — widget intégré dans les modules

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="w-8 h-8 border-2 border-[#F2F2F7] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-black"><div className="w-8 h-8 border-2 border-[#F2F2F7] border-t-transparent animate-spin" /></div>;
  // 14/10/2026 — l'adresse demandée est gardée (état de navigation) : le lien de levée
  // de doute d'un SMS revient à sa page après la connexion (lib/retourConnexion).
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-black"><div className="w-8 h-8 border-2 border-[#F2F2F7] border-t-transparent animate-spin" /></div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

/**
 * AccessDenied — panneau affiché quand le rôle n'autorise pas la route.
 * Défense en profondeur côté produit : l'autorité reste le backend (RBAC-1),
 * qui renvoie 403 sur les routes RH PII. Ici on évite simplement d'afficher un
 * écran qui partirait en 403 (UX) et de laisser croire que la donnée existe.
 */
function AccessDenied() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="text-5xl mb-3">🔒</div>
        <h1 className="text-lg font-semibold text-white mb-1">Accès refusé</h1>
        <p className="text-sm text-neutral-400">
          Votre rôle ne vous autorise pas à consulter cette section. Contactez un
          administrateur si vous pensez que c'est une erreur.
        </p>
      </div>
    </div>
  );
}

/**
 * RequireRole — garde de navigation par RÔLE (2e couche, le backend RBAC-1 reste
 * l'autorité). Rend les enfants si `user.role ∈ roles`. Sinon `AccessDenied`.
 *
 * `allowSelf` : pour les écrans self-service à paramètre `:id` (fiche d'un
 * collaborateur), autorise aussi le propriétaire (user.employee_id === params.id)
 * via `canViewEmployee`. Fail-closed tant que /auth/me n'expose pas employee_id
 * (voir AuthContext) : seuls les RH passent — sans impact aujourd'hui (comptes admin).
 */
function RequireRole({ roles, allowSelf = false, children }) {
  const { user, loading, hasRole, canViewEmployee } = useAuth();
  const { id } = useParams();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-black"><div className="w-8 h-8 border-2 border-[#F2F2F7] border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  const ok = hasRole(roles) || (allowSelf && canViewEmployee(id));
  return ok ? children : <AccessDenied />;
}

/**
 * RequireAccess — garde de navigation par la MATRICE d'accès A0 (source unique).
 * Rend les enfants si le compte peut OUVRIR `page` (et, si `write`, s'il n'est pas
 * en lecture seule). Le backend (A1) reste l'autorité (403). `allowSelf` autorise
 * en plus le propriétaire d'une fiche (:id) — self-service collaborateur.
 * NB : le cloisonnement Finance est déjà porté par `canOpen` (page finance ⇒ tier
 * finance requis), pas besoin de le redire ici.
 */
function RequireAccess({ page, write = false, allowSelf = false, children }) {
  const { user, loading, canOpen, canWrite, canViewEmployee } = useAuth();
  const { id } = useParams();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-black"><div className="w-8 h-8 border-2 border-[#F2F2F7] border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  const ok = (canOpen(page) && (!write || canWrite)) || (allowSelf && canViewEmployee(id));
  return ok ? children : <AccessDenied />;
}

/**
 * RedirectWithSearch — redirige vers `to` en PRÉSERVANT la query string (?section=…).
 * Sert à dédupliquer les anciens écrans /admin/* vers l'Administration unifiée sans
 * casser les deep-links existants (DPAE/Factures/Devis pointent ?section=…).
 */
function RedirectWithSearch({ to }) {
  const loc = useLocation();
  return <Navigate to={{ pathname: to, search: loc.search }} replace />;
}

// 04/10/2026 (lot C) — /administration?tab=… : les notifications d'incident de
// tâche automatique pointent vers « /administration?tab=crons ». L'index
// renvoyait toujours vers « comptes » en PERDANT la requête : le lien
// n'atterrissait jamais sur les tâches. On aiguille désormais selon `tab`
// (requête conservée) ; sans `tab` connu, la page des comptes comme avant.
const ADMIN_TAB_VERS_PAGE = {
  crons: 'architecture',
  architecture: 'architecture',
  comptes: 'comptes',
  entreprise: 'entreprise',
  rgpd: 'rgpd',
  systeme: 'systeme',
  agents: 'agents',
  'gouvernance-ia': 'gouvernance-ia',
};

function AdministrationIndexRedirect() {
  const loc = useLocation();
  const tab = new URLSearchParams(loc.search).get('tab') || '';
  const page = ADMIN_TAB_VERS_PAGE[tab] || 'comptes';
  return <Navigate to={{ pathname: `/administration/${page}`, search: loc.search }} replace />;
}

// Page d'atterrissage → route réelle. Ordre = celui du menu (lib/permissions.PAGE_CATALOG),
// donc la page la plus « générale » que le compte possède gagne.
const ROUTE_PAR_PAGE = [
  ['inbox', '/inbox'],
  ['planning', '/planning'],
  ['terrain', '/terrain'],
  ['sondages', '/sondages'],
  ['sites', '/sites'],
  ['employes', '/hr/employees'],
  ['clients', '/clients'],
  ['appels-offre', '/appels-offre'],
  ['compliance', '/hr/compliance'],
  ['dpae', '/hr/dpae'],
  ['heures', '/rh/heures'],
  ['hr', '/hr'],
  ['paie', '/rh/paie'],
  ['ia', '/ia'],
  ['rentabilite', '/rentabilite'],
  ['devis', '/devis'],
  ['factures', '/factures'],
  ['crm', '/crm'],
];

/**
 * AccueilSelonProfil — la page d'accueil du compte, pas celle d'un autre.
 *
 * 15/07 (retour Théo, capture d'Armen) — Après connexion, tout le monde atterrissait sur
 * `/` = le Dashboard. Or `dashboard` n'est PAS dans les pages du profil « Responsable
 * d'exploitation » (ni du « Chef d'équipe ») : ils se prenaient donc « Accès refusé » DÈS
 * LA CONNEXION, avant d'avoir cliqué quoi que ce soit — le cadenas sur la capture d'Armen,
 * c'est ça. Le cloisonnement faisait son travail ; c'est l'atterrissage qui était faux.
 *
 * On envoie chacun sur la première page qu'il possède RÉELLEMENT, au lieu de lui claquer
 * une porte au visage. Corrige aussi tous les profils futurs sans y repenser.
 * `canOpen` = la même autorité que le menu et RequireAccess — jamais une 2e logique.
 */
function AccueilSelonProfil() {
  const { user, loading, canOpen } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-black"><div className="w-8 h-8 border-2 border-[#F2F2F7] border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (canOpen('dashboard')) return <DashboardHome />;
  const premiere = ROUTE_PAR_PAGE.find(([page]) => canOpen(page));
  // Aucune page du tout = compte sans aucun accès web (ex. agent terrain qui se trompe
  // d'adresse). Là, le refus est la réponse HONNÊTE — il n'y a nulle part où l'envoyer.
  return premiere ? <Navigate to={premiere[1]} replace /> : <AccessDenied />;
}

function App() {
  return (
    <AuthProvider>
      <TaskProvider>
        <BrowserRouter>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
              <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
              <Route path="/signer/:token" element={<SignaturePage />} />
              <Route path="/" element={<ProtectedRoute><PrefsProvider><ReglagesRhProvider><DashboardLayout /></ReglagesRhProvider></PrefsProvider></ProtectedRoute>}>
                {/* A0 — chaque route lit la MATRICE d'accès (RequireAccess page=block).
                    Le backend (A1) reste l'autorité 403 ; ceci est la 2e couche + la
                    redirection propre (AccessDenied) sur entrée URL directe. */}
                <Route index element={<AccueilSelonProfil />} />
                {/* Alertes de poste (23/04 P2-B) — messagerie WhatsApp groupée individualisée (RH) */}
                <Route path="alertes-poste" element={<RequireAccess page="employes"><AlertesPoste /></RequireAccess>} />
                <Route path="ia" element={<RequireAccess page="ia"><IAHubPage /></RequireAccess>} />
                <Route path="inbox" element={<RequireAccess page="inbox"><InboxPage /></RequireAccess>} />
                <Route path="suivi" element={<RequireAccess page="suivi"><SuiviDirectionPage /></RequireAccess>} />
                {/* 30/04 — /crm pointe sur la page unifiée (tabs Prospects + Campagnes) */}
                {/* v3/F1 (11/10/2026) — /crm = page Commercial v3 ; /crm/ancien = l'ancienne page unifiée (un mois) */}
                <Route path="crm" element={<RequireAccess page="crm"><CommercialPage /></RequireAccess>} />
                <Route path="crm/ancien" element={<RequireAccess page="crm"><CRMUnifiedPage /></RequireAccess>} />
                {/* Legacy : route directe vers CRMPage si besoin de rollback rapide */}
                {/* ☠ /crm/legacy SUPPRIMÉE 31/08 (Q9, GO Théo) — l'unifiée rend déjà CRMPage embarquée */}
                {/* ☠ /crm/ai SUPPRIMÉE 31/08 (Q9, GO Théo) — zéro navigation — décision Théo 31/08 (lot Q9) */}
                {/* ☠ /crm/campagnes SUPPRIMÉE 31/08 (Q9, GO Théo) — accessible seulement depuis /crm/legacy — meurt avec elle */}
                {/* Clients */}
                <Route path="clients" element={<RequireAccess page="clients"><Clients /></RequireAccess>} />
                <Route path="clients/new" element={<RequireAccess page="clients" write><ClientForm /></RequireAccess>} />
                {/* 07/09 — `/clients/:id` ouvre désormais LA FICHE (lecture, tout au
                    même endroit) ; le formulaire prend `/clients/:id/edit`, comme
                    `devis/:id/edit` et `factures/:id/edit`. La liste continue
                    d'ouvrir le formulaire EN MODALE (elle ne passe pas par la
                    route) : rien de ce qui existait ne se ferme. */}
                <Route path="clients/:id" element={<RequireAccess page="clients"><FicheClient /></RequireAccess>} />
                <Route path="clients/:id/edit" element={<RequireAccess page="clients"><ClientForm /></RequireAccess>} />
                {/* Sites */}
                <Route path="sites" element={<RequireAccess page="sites"><Sites /></RequireAccess>} />
                <Route path="sites/tableau" element={<RequireAccess page="sites" write><SitesTableauRemuneration /></RequireAccess>} />
                <Route path="sites/new" element={<RequireAccess page="sites" write><SiteForm /></RequireAccess>} />
                <Route path="sites/:id" element={<RequireAccess page="sites"><SiteForm /></RequireAccess>} />
                {/* Planning */}
                <Route path="planning" element={<RequireAccess page="planning"><Planning /></RequireAccess>} />
                <Route path="terrain" element={<RequireAccess page="terrain"><TerrainPage /></RequireAccess>} />  {/* PHASE 4 — réception activité terrain */}
                {/* Retours 13/07 — page dédiée par agent (pas d'allowSelf : endpoints /api/terrain/* require_rh) */}
                <Route path="terrain/agents/:id" element={<RequireAccess page="terrain"><TerrainAgentPage /></RequireAccess>} />
                {/* 14/10/2026 — lien de levée de doute des SMS/e-mails d'alerte : ProtectedRoute seul (un responsable
                    d'équipe sans accès « terrain » doit pouvoir traiter SON alerte) ; le droit est jugé par l'API. */}
                <Route path="levee-de-doute/:token" element={<PtiLeveeDoutePage />} />
                {/* Retours 13/07 — Main courante unifiée ; /comptes-rendus redirige (routes backend intactes) */}
                <Route path="main-courante" element={<RequireAccess page="terrain"><MainCourantePage /></RequireAccess>} />
                {/* 17/08 — Écarts de pointage : ouvre le hub Terrain sur l'onglet d'arbitrage.
                    Gardé par la page `terrain` (déjà accordée au responsable d'exploitation, qui
                    est celui qui arbitre) — pas par une nouvelle clé d'accès qu'il aurait fallu
                    cocher compte par compte avant que l'écran serve à quelqu'un. */}
                <Route path="terrain/ecarts-pointage" element={<RequireAccess page="terrain"><EcartsPointagePage /></RequireAccess>} />
                <Route path="comptes-rendus" element={<Navigate to="/main-courante" replace />} />
                {/* SPRINT 2 — sondages/notifications agents + disponibilités déclarées (vue bureau) */}
                <Route path="sondages" element={<RequireAccess page="sondages"><SondagesDispoPage initialTab="sondages" /></RequireAccess>} />
                <Route path="disponibilites" element={<RequireAccess page="sondages"><SondagesDispoPage initialTab="disponibilites" /></RequireAccess>} />
                {/* Devis (Finance — masqué à l'exploitation via canOpen) */}
                <Route path="devis" element={<RequireAccess page="devis"><Devis /></RequireAccess>} />
                <Route path="devis/new" element={<RequireAccess page="devis" write><DevisForm /></RequireAccess>} />
                <Route path="devis/nouveau" element={<RequireAccess page="devis" write><DevisForm /></RequireAccess>} />
                <Route path="devis/:id" element={<RequireAccess page="devis"><DevisForm /></RequireAccess>} />
                <Route path="devis/:id/edit" element={<RequireAccess page="devis" write><DevisForm /></RequireAccess>} />
                {/* Factures (Finance) */}
                <Route path="factures" element={<RequireAccess page="factures"><Factures /></RequireAccess>} />
                <Route path="factures/sous-traitants" element={<RequireAccess page="factures"><FacturesSousTraitants /></RequireAccess>} />
                {/* 06/10/2026 — lot C : relevés, factures d'achat, CA3, DSN. Depuis le 22/10/2026 : le serveur exige le RÔLE fondateur, sans code à 6 chiffres. */}
                <Route path="finance/depots" element={<RequireAccess page="rentabilite"><DeposerPieces /></RequireAccess>} />
                {/* 13/10/2026 — Pilotage (« go pilotage, on le met en place ») : 6 onglets (dont « Mois bouclés »
                    depuis le 22/10/2026), calcul de nuit. Même garde que le dépôt : le serveur exige le RÔLE
                    fondateur, sans code à 6 chiffres (utils/acces_fondateur ; /api/pilotage et /api/gc/pilotage). */}
                <Route path="pilotage" element={<RequireAccess page="rentabilite"><PilotagePage /></RequireAccess>} />
                <Route path="factures/new" element={<RequireAccess page="factures" write><FactureForm /></RequireAccess>} />
                <Route path="factures/nouvelle" element={<RequireAccess page="factures" write><FactureForm /></RequireAccess>} />
                <Route path="factures/:id" element={<RequireAccess page="factures"><FactureForm /></RequireAccess>} />
                <Route path="factures/:id/edit" element={<RequireAccess page="factures" write><FactureForm /></RequireAccess>} />
                {/* ☠ Banque SUPPRIMÉE 31/08 (décision Théo : « y a rien ») */}
                {/* RH — routes sensibles PII, gardées par la matrice (2e couche).
                    L'autorité reste le backend (403). La fiche collaborateur autorise
                    aussi le propriétaire (self-service) via allowSelf. */}
                <Route path="hr" element={<RequireAccess page="hr"><HRPage /></RequireAccess>} />
                <Route path="hr/employees" element={<RequireAccess page="employes"><EmployeesPage /></RequireAccess>} />
                <Route path="collaborateurs/:id/fiche" element={<RequireAccess page="employes" allowSelf><FicheCollaborateur /></RequireAccess>} />
                <Route path="hr/compliance" element={<RequireAccess page="compliance"><ComplianceDashboard /></RequireAccess>} />
                <Route path="hr/dpae" element={<RequireAccess page="dpae"><DPAEPage /></RequireAccess>} />
                <Route path="hr/documents" element={<RequireAccess page="documents"><DocumentsPage /></RequireAccess>} />
                <Route path="rentabilite" element={<RequireAccess page="rentabilite"><RentabilitePage /></RequireAccess>} />
                <Route path="rh/heures" element={<RequireAccess page="heures"><SuiviHeuresPage /></RequireAccess>} />
                <Route path="rh/paie" element={<RequireAccess page="paie"><PaiePage /></RequireAccess>} />
                <Route path="social" element={<RequireAccess page="crm"><SocialMediaPage /></RequireAccess>} />
                <Route path="communication" element={<RequireAccess page="crm"><CommunicationPage /></RequireAccess>} />
                {/* Réglages perso — accessibles à tout compte web (jamais gardé par page) */}
                <Route path="settings" element={<SettingsPage />} />
                {/* Refonte 23/06 — module Administration : 1 entrée sidebar + sous-pages en routes imbriquées (fondateur uniquement) */}
                <Route path="administration" element={<RequireRole roles={['admin']}><AdministrationPage /></RequireRole>}>
                  <Route index element={<AdministrationIndexRedirect />} />
                  <Route path="comptes" element={<UsersAdminPage />} />
                  <Route path="entreprise" element={<SettingsAdminPage />} />
                  <Route path="rgpd" element={<RgpdAdminPage />} />
                  <Route path="systeme" element={<AdminHealthPage />} />
                  <Route path="architecture" element={<AdminArchitecturePage />} />
                  <Route path="agents" element={<AgentsDashboard />} />
                  {/* Page de Contrôle des IA — gouvernance des écritures agents (fondateur L1, gate GC côté page) */}
                  <Route path="gouvernance-ia" element={<AgentGovernancePage />} />
                </Route>
                {/* A0 — dédup des 6 écrans admin qui se chevauchaient : les anciennes
                    routes /admin/* REDIRIGENT désormais vers l'Administration unifiée
                    (mêmes composants, une seule copie). Query string préservée pour ne
                    pas casser les deep-links (?section=urssaf/facturation). */}
                <Route path="admin/rgpd" element={<RedirectWithSearch to="/administration/rgpd" />} />
                <Route path="admin/settings" element={<RedirectWithSearch to="/administration/entreprise" />} />
                <Route path="admin/users" element={<RedirectWithSearch to="/administration/comptes" />} />
                <Route path="admin/health" element={<RedirectWithSearch to="/administration/systeme" />} />
                <Route path="admin/architecture" element={<RedirectWithSearch to="/administration/architecture" />} />
                <Route path="admin/agents" element={<RedirectWithSearch to="/administration/agents" />} />
                {/* Arbitrage : pas de jumeau dans /administration → route conservée telle quelle. */}
                <Route path="admin/arbitrage" element={<RequireRole roles={['admin']}><ArbitrageHubPage /></RequireRole>} />  {/* chantier C — arbitrage conflits/doublons */}
                {/* L6.C — Pages IA legacy : redirects vers le Hub /ia */}
                <Route path="agent" element={<Navigate to="/ia?tab=chat" replace />} />
                <Route path="expertise-agents" element={<Navigate to="/ia?tab=overview" replace />} />
                <Route path="feedback" element={<Navigate to="/ia?tab=validation" replace />} />
                <Route path="multi-agents" element={<Navigate to="/ia" replace />} />
                {/* 11/10/2026 — la page /1806 est SUPPRIMÉE (décision de Théo). Ce n'est
                    pas elle : un simple renvoi, pour les notifications déjà en base qui
                    pointent encore vers /1806 (validations IA, revue commerciale) — elles
                    ouvraient une page blanche. La file de validation vit dans
                    Administration › Contrôle IA. */}
                <Route path="1806" element={<Navigate to="/administration/gouvernance-ia" replace />} />
                {/* FUSION 20/08 : Marge = 1er onglet de Rentabilité — l'ancienne
                    route redirige (même précédent que /comptes-rendus). */}
                <Route path="marge" element={<Navigate to="/rentabilite" replace />} />
                {/* /agent-network expose le taux d'achat (marge confidentielle) — RH/admin
                    uniquement (audit 14/07). Pas de page-block dédié → garde par rôle RH. */}
                <Route path="agent-network" element={<RequireRole roles={RH_ROLES}><AgentNetworkPage /></RequireRole>} />
                {/* 30/04 — /appels-offre pointe sur la page unifiée (tabs Liste/Réponse/Exemples/Importer) */}
                <Route path="appels-offre" element={<RequireAccess page="appels-offre"><AppelsOffreUnifiedPage /></RequireAccess>} />
                {/* Legacy : routes directes pour rollback rapide */}
                {/* ☠ /appels-offre/legacy SUPPRIMÉE 31/08 (Q9, GO Théo) — l'unifiée rend déjà AppelsOffrePage embarquée */}
                {/* ☠ /appels-offre/base-reponses SUPPRIMÉE 31/08 (Q9, GO Théo) — l'unifiée rend déjà BaseReponsesPage */}
                <Route path="appels-offre/:aoId/reponse" element={<RequireAccess page="appels-offre"><AOReponsePage /></RequireAccess>} />
                {/* Route standalone doublons supprimée — widget intégré dans chaque module */}
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        {/* 17/09 — la popup « Fichier verrouillé ». Montée une fois, ici : elle
            s'ouvre seule sur toute réponse 423 du serveur (lib/api.js), quel que
            soit l'écran, et propose le code au fondateur ou renvoie vers lui. */}
        <DialogueCadenasVerrouille />
      </TaskProvider>
    </AuthProvider>
  );
}
export default App;
